import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BooksSummary, Gstr2BResponse, Gstr2BStats, SuppliersReport } from '../../models/gstr-2b-response.model';
import { ChartOptions } from 'chart.js';

@Component({
  selector: 'app-gstr-response',
  templateUrl: './gstr-response.component.html',
  styleUrl: './gstr-response.component.scss'
})
export class GstrResponseComponent implements OnChanges {

  @Input() input: Gstr2BResponse | undefined;
  @Input() downloadFile = false;

  bookSummary: BooksSummary | undefined;
  chartData:any | undefined;
  isImportGst:boolean = false;
  gstChartMatched:any[] = [];
  gstChartSummary:any[] = [];
  suppliers_report: SuppliersReport | undefined;

  pieChartOptions: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      }
    },
    scales: {
      x: {
        display: false, // Hide X-axis
      },
      y: {
        display: false, // Hide Y-axis
      },
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['input']?.currentValue) {
      this.bookSummary = this.getBookSummary(this.input?.stats);
      this.chartData = this.getChartData(this.input?.stats?.books_summary);
      this.gstChartMatched = this.getGstChartData(this.input?.stats?.books_summary);
      this.gstChartSummary = this.getGstChartSummary(this.input?.stats);
      this.suppliers_report = this.getSuppliersReport(this.input?.suppliersReport);

      if(this.input?.matchingType === 'IMPORT'){
        this.isImportGst = true;
      } else {
        this.isImportGst = false;
      }
    }
  }

  getBookSummary(input: Gstr2BStats | undefined) {
    if (!input) {
      return undefined;
    }
    return input.books_summary;
  }


  getSuppliersReport(input:any) {
    return this.input?.suppliersReport;
  }

  getChartData(bookSummary: BooksSummary | undefined){
    if(!bookSummary){
      return undefined;
    }
    return {
      labels: [`Matched : ${bookSummary.mapped_percentage.toFixed(1)}%`, `Unmatched : ${bookSummary.unmapped_percentage.toFixed(1)}%`],
      datasets: [
          {
              data: [bookSummary.mapped_percentage, bookSummary.unmapped_percentage],
              backgroundColor: ['#32CC77', '#5539A8'],
          }
      ]
  };

  }

  getGstChartSummary(stats: Gstr2BStats | undefined){
    if(!stats){
      return []
    }
    const igstData = [
      stats.perfect_match.total_mapped.igst+ stats.perfect_match_with_tolerance.total_mapped.igst,
      stats.probable_match.total_mapped.igst,
      stats.perfect_match_without_special.total_mapped.igst+ stats.perfect_match_without_special_and_tolerance.total_mapped.igst
    ];
    const cgstData = [
      stats.perfect_match.total_mapped.cgst+ stats.perfect_match_with_tolerance.total_mapped.cgst,
      stats.probable_match.total_mapped.cgst,
      stats.perfect_match_without_special.total_mapped.cgst+ stats.perfect_match_without_special_and_tolerance.total_mapped.cgst
    ];
    const sgstData = [
      stats.perfect_match.total_mapped.sgst+ stats.perfect_match_with_tolerance.total_mapped.sgst,
      stats.probable_match.total_mapped.sgst,
      stats.perfect_match_without_special.total_mapped.sgst + stats.perfect_match_without_special_and_tolerance.total_mapped.sgst
    ];

    const totalIgst = igstData.reduce((acc, curr) => acc + curr, 0);
    const totalCgst = cgstData.reduce((acc, curr) => acc + curr, 0);
    const totalSgst = sgstData.reduce((acc, curr) => acc + curr, 0);

    const igst = {
      "title": "IGST",
      "chartType": "doughnut",
      "options": this.pieChartOptions,
      "data": {
        labels: [
          `Perfect Match : ${this.getPercentage(igstData[0], totalIgst)}`,
          `Probable Match : ${this.getPercentage(igstData[1], totalIgst)}`,
          `Nearest Match : ${this.getPercentage(igstData[2], totalIgst)}`
        ],
        datasets: [
            {
                data: igstData,
                backgroundColor: ['#32CC77', '#5539A8', '#64748B'],
            }
        ]
      }
    }
    const cgst = {
      "title": "CGST",
      "chartType": "doughnut",
      "options": this.pieChartOptions,
      "data": {
        labels: [
          `Perfect Match : ${this.getPercentage(cgstData[0], totalCgst)}`,
          `Probable Match : ${this.getPercentage(cgstData[1], totalCgst)}`,
          `Nearest Match : ${this.getPercentage(cgstData[2], totalCgst)}`
        ],
        datasets: [
            {
                data: cgstData,
                backgroundColor: ['#32CC77', '#5539A8', '#64748B'],
            }
        ]
      }
    }
    const sgst = {
      "title": "SGST",
      "chartType": "doughnut",
      "options": this.pieChartOptions,
      "data": {
        labels: [
          `Perfect Match : ${this.getPercentage(sgstData[0], totalSgst)}`,
          `Probable Match : ${this.getPercentage(sgstData[1], totalSgst)}`,
          `Nearest Match : ${this.getPercentage(sgstData[2], totalSgst)}`
        ],
        datasets: [
            {
                data: sgstData,
                backgroundColor: ['#32CC77', '#5539A8', '#64748B'],
            }
        ]
      }
    }

    const gstData:any[] = [igst, cgst, sgst];
    return gstData;
  }
  getGstChartData(bookSummary: BooksSummary | undefined){
    if(!bookSummary){
      return []
    }
    const igst = {
      "title": "IGST",
      "chartType": "doughnut",
      "options": this.pieChartOptions,
      "data": {
        labels: [`Matched : ${this.getPercentage(bookSummary.matched_entries.igst, bookSummary.total_entries.igst)}`, `Unmatched : ${this.getPercentage(bookSummary.unmatched_entries.igst, bookSummary.total_entries.igst)}`],
        datasets: [
            {
                data: [bookSummary.matched_entries.igst, bookSummary.unmatched_entries.igst],
                backgroundColor: ['#32CC77', '#5539A8', '#64748B'],
            }
        ]
      }
    }
    const cgst = {
      "title": "CGST",
      "chartType": "doughnut",
      "options": this.pieChartOptions,
      "data": {
        labels: [`Matched : ${this.getPercentage(bookSummary.matched_entries.cgst, bookSummary.total_entries.cgst)}`, `Unmatched : ${this.getPercentage(bookSummary.unmatched_entries.cgst, bookSummary.total_entries.cgst)}`],
        datasets: [
            {
                data: [bookSummary.matched_entries.cgst, bookSummary.unmatched_entries.cgst],
                backgroundColor: ['#32CC77', '#5539A8', '#64748B'],
            }
        ]
      }
    }
    const sgst = {
      "title": "SGST",
      "chartType": "doughnut",
      "options": this.pieChartOptions,
      "data": {
        labels: [`Matched : ${this.getPercentage(bookSummary.matched_entries.sgst, bookSummary.total_entries.sgst)}`, `Unmatched : ${this.getPercentage(bookSummary.unmatched_entries.sgst, bookSummary.total_entries.sgst)}`],
        datasets: [
            {
                data: [bookSummary.matched_entries.sgst, bookSummary.unmatched_entries.sgst],
                backgroundColor: ['#32CC77', '#5539A8', '#64748B'],
            }
        ]
      }
    }
    const gstData:any[] = [igst, cgst, sgst];
    return gstData;
  }

  getPercentage(value: number, total: number){
    const percentage = (value*100)/total;
    const calculatedValue = `${percentage.toFixed(1)}%`;
    return calculatedValue;
  }

}
